import { useState, useMemo, useEffect } from "react";

import append from "ramda/src/append";
import reject from "ramda/src/reject";
import propEq from "ramda/src/propEq";
import prop from "ramda/src/prop";
import Api from "../../services/Api";

import useAuth from "../../routes/login/AuthContext";
import { SearchNetworkByName } from "../../services/NetworkApi";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useNetwork } from "../../hooks/useNetwork";
import { useNetworkContext } from "../../contexts/NetworkContext/context";
import { useDebounce } from "../../hooks/useDebounce";
import { normalize } from "../../helpers/normalize";
import { useSnackbar } from "../../hooks/useSnackbar";

export const useCopyHandler = (props) => {
  const { setIsOpen } = useModalContext();
  const [isLoading, setIsLoading] = useState(false);
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const { getL1, getL2, getL3 } = useNetwork();
  const [networks, setNetworks] = useState([]);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const { getAuthToken } = useAuth();
  const [isNetworksLoading, setIsNetworksLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedValue = useDebounce(searchValue, 500);

  useEffect(() => {
    if (debouncedValue.length < 3) {
      setNetworks([]);
      return;
    }

    const getNetworks = async () => {
      setIsNetworksLoading(true);

      const results = await SearchNetworkByName(
        debouncedValue,
        await getAuthToken()
      );

      if (results) {
        setNetworks(results);
      }

      setIsNetworksLoading(false);
    };

    getNetworks();
  }, [normalize(debouncedValue)]);

  const l1 = useMemo(() => {
    if (!props.l1Id) return;

    return getL1(props.l1Id);
  }, [props.l1Id]);

  const l2 = useMemo(() => {
    if (!props.l1Id || !props.l2Id) return;

    return getL2(props.l1Id, props.l2Id);
  }, [props.l1Id, props.l2Id]);

  const l3 = useMemo(() => {
    if (!props.l1Id || !props.l2Id || !props.l3Id) return;

    return getL3(props.l1Id, props.l2Id, props.l3Id);
  }, [props.l1Id, props.l2Id, props.l3Id]);

  const handleUpdate = async () => {
    setIsLoading(true);
    const response = await Api.requestCopyQuestions(
      props.l1Id,
      props.l2Id,
      props.l3Id,
      selectedNetworks.map(({ id }) => ({ l1Id: id })),
      await getAuthToken()
    );

    if (!response.error) {
      setIsOpen(false);
      showSuccessSnackbar("Successfully copied");
      setSelectedNetworks([]);
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    setIsLoading(false);
    loadNetworkInfoForNetworks(selectedNetworks.map(prop("id")));
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 8) {
      setSearchValue("");
    }
  };

  const handleNetworkSelect = (id) => async () => {
    const selectedNetwork = networks.find(propEq("id", id));

    if (selectedNetwork) {
      setSelectedNetworks(append(selectedNetwork));
    }

    setSearchValue("");
  };

  const handleChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFocus = () => {
    setSearchValue("");
  };

  const handleDeleteChip = (id) => () =>
    setSelectedNetworks(reject(propEq("id", id)));

  return {
    l1,
    l2,
    isNetworksLoading,
    searchValue,
    l3,
    networks,
    isLoading,
    handleUpdate,
    selectedNetworks,
    handleFocus,
    handleDeleteChip,
    handleKeyDown,
    handleChange,
    handleNetworkSelect,
  };
};